import PQueue from 'p-queue';
import { SnackBarHelper } from '@lyearn-team/frontend-components';
import handleLogout from 'helper/handleLogout';
import * as TinyPubSub from '../TinyPubSub';
import { navigatorOnlineCallback } from '../navigatorOnlineCallback';
import User from '../currentUser';
import * as databaseLock from '../lock';
import { blockUserInteraction } from '../blockUserInteractionHelper';

import showUserTakeControl from '../../components/UserTakeControl';
import showUserTakeControlDark from '../../components/UserTakeControlDark';
import kinesisPutRecord from '../kinesisPutRecord';
import {
  DataSyncStatus,
  getMultipleStatesById,
  getLatestUnprocessedDataSyncObject,
  removeDataSyncObjectWithId,
  getDataSyncObjectById,
  saveStates,
  getMultipleStateInteractionsById,
  getMultipleEnrolmentsById,
  saveEnrolment,
  saveStateInteractions
} from './LocalStore';

import updateDSOFieldAndSave from './utils/updateDSOFieldAndSave';
// eslint-disable-next-line import/no-cycle
import { initBgSyncService } from './backgroundSyncAndRepair';
// eslint-disable-next-line import/no-cycle
import { initBgSyncServiceFirebase } from './backgroundSyncAndRepair/firebaseSync';
import handleErrorDSOs from './utils/handleErrorDSOs';
import callTakeControlAPI from './utils/callTakeControlAPI';
import incrementVersionNumber from './utils/incrementVersionNumber';
import addVersionId from './utils/addVersionId';

const user = new User();
const syncQueue = new PQueue({ concurrency: 1 });

window.syncQueue = syncQueue;

export async function fetchAndSaveUpdatedStateEnrolmentsStateInteractions(
  stateIds,
  enrolmentIds,
  stateInteractionIds
) {
  await databaseLock.acquire();
  const states = await getMultipleStatesById(stateIds);
  const enrolments = await getMultipleEnrolmentsById(enrolmentIds);
  const stateInteractions = await getMultipleStateInteractionsById(stateInteractionIds);

  const versionedStates = await Promise.all(states.map(incrementVersionNumber).map(addVersionId));
  const versionedEnrolments = await Promise.all(
    enrolments.map(incrementVersionNumber).map(addVersionId)
  );
  const versionedStateInteractions = stateInteractions.map(incrementVersionNumber);

  await saveStates(versionedStates);
  await saveEnrolment(versionedEnrolments);
  await saveStateInteractions(versionedStateInteractions);
  await databaseLock.unlock();
}

async function syncCore() {
  // Get latest unprocessed dataSyncObject
  //   - return if nothing is unprocessed
  // Create kinesisObject from dataSyncObject
  //   - Change stateIds to actual stateDocuments (same for enrolments and stateInteractions)
  //   - Add partionKey, streamName and other filler properties
  // Write to kinesis
  //   if (success)
  //     Update kinesisWriteTS
  //     Update status to PROCESSED
  //     Increment version number of states
  //   else
  //     Call sync again to retry
  // Call sync again to proceed with any remaining datasync objects

  const dataSyncObject = await getLatestUnprocessedDataSyncObject();
  if (!dataSyncObject) {
    return;
  }

  const updatedStates = await getMultipleStatesById(dataSyncObject.stateIds);
  const updatedStateInteractions = await getMultipleStateInteractionsById(
    dataSyncObject.stateInteractionIds
  );
  const updatedEnrolments = await getMultipleEnrolmentsById(dataSyncObject.enrollmentIds);
  const userId = dataSyncObject.userId || user.id();

  const versionInfoStates = await Promise.all(
    updatedStates.map(incrementVersionNumber).map(addVersionId)
  );
  const versionInfoEnrolments = await Promise.all(
    updatedEnrolments.map(incrementVersionNumber).map(addVersionId)
  );
  const versionInfoStateInteractions = updatedStateInteractions.map(incrementVersionNumber);

  const recordObject = {
    data: {
      dataSyncId: dataSyncObject.id,
      userId,
      commands: dataSyncObject.commands,
      events: dataSyncObject.events,
      updatedStates: versionInfoStates,
      updatedEnrolments: versionInfoEnrolments,
      updatedStateInteractions: versionInfoStateInteractions,
      timeStamp: dataSyncObject.timeStamp || new Date().toISOString(),
      createdFromSessionToken: dataSyncObject.createdFromSessionToken,
      sentFromFromSessionToken: window.userSessionId
    },
    origin: window.location.origin,
    partitionKey: userId,
    streamName: 'lyearn-datasync-stream'
  };

  try {
    const response = await kinesisPutRecord(recordObject);
    if (response && response.ok) {
      await updateDSOFieldAndSave(dataSyncObject, {
        status: DataSyncStatus.PROCESSED,
        kinesisWriteTS: Date.now()
      });
      await fetchAndSaveUpdatedStateEnrolmentsStateInteractions(
        dataSyncObject.stateIds,
        dataSyncObject.enrollmentIds,
        dataSyncObject.stateInteractionIds
      );

      /* window.analytics &&
        window.analytics.trackBatch(
          'KINESIS_REQ_SUCCESS',
          {
            dataSyncId: dataSyncObject.id,
            url: window.location.href
          },
          3
        ); */
    } else if (response.status === 403) {
      // show user blocker
      blockUserInteraction(true);

      window.analytics.track('RECEIVED_403_FROM_KINESIS_API', {
        session_token: window.userSessionId,
        url: window.location.href,
        recordObject: JSON.stringify(recordObject),
        commands: dataSyncObject.commands
      });

      const responseJSON = await response.json();
      if (responseJSON.message === 'fromAdminChange') {
        await callTakeControlAPI({ userId });
      } else if (responseJSON.message === 'takeControl') {
        const isDarkTheme = window.__getFeatureFlagFunc('dark-theme');
        const takeControlResponse = isDarkTheme
          ? await showUserTakeControlDark()
          : await showUserTakeControl();
        if (takeControlResponse === 'OK') {
          await callTakeControlAPI({ userId });
        } else if (takeControlResponse === 'CANCEL') {
          window.location.href = '/#/learner';
        }
      }

      blockUserInteraction(false);
    }
  } catch (e) {
    console.log('Error in kinesisPutRecord', e);
    SnackBarHelper.showSnackbar({
      label: 'Error occurred while saving your progress, please reload',
      duration: 15,
      buttons: [{ onClick: () => window.location.reload(true), label: 'Reload' }]
    });

    window.analytics &&
      window.analytics.trackBatch(
        'KINESIS_WRITE_REQ_FAILED',
        {
          errorMessage: e && e.toString(),
          dataSyncId: recordObject.data.dataSyncId,
          dsoCommands: JSON.stringify(recordObject.data.commands)
        },
        3
      );
    e && window.bugsnagClient && window.bugsnagClient.notify(e, { metaData: { recordObject } });
  } finally {
    // eslint-disable-next-line no-use-before-define
    setTimeout(sync, 1000);
  }
}

export async function sync() {
  if (!navigator.onLine) {
    navigatorOnlineCallback('syncFunc', () => sync());
    return;
  }

  syncQueue.add(syncCore);
}

const subscriptionCallback = async response => {
  const firebaseSync = window.__getFeatureFlagFunc('firebase-subscription');
  let responseData = null;
  if (firebaseSync) {
    responseData = response;
  } else {
    responseData = response.data.dataSyncMutationSubscription;
  }

  // MS:todo status me ERROR,message me COMPANYID_NOT_FOUND
  const { dataSyncId, status = 'success', message } = responseData;
  const dataSyncObject = await getDataSyncObjectById(dataSyncId);
  if (!dataSyncObject) {
    return;
  }
  /* window.analytics &&
    window.analytics.trackBatch(
      'FIREBASE_RECEIVED_SUBSCRIPTION_CALLBACK',
      {
        dataSyncId,
        status,
        url: window.location.href,
        responseData
      },
      3
    ); */
  if (status.toLowerCase() === 'success') {
    await removeDataSyncObjectWithId(dataSyncId);
    TinyPubSub.publish('oneDataSyncObjectsUpdated'); // eslint-disable-line new-cap
  } else {
    if (!window.isActive) {
      return;
    }

    if (status.toLowerCase() === 'error' && message.includes('COMPANYID_NOT_FOUND')) {
      const error = new Error('CompanyId error in subscription response');

      error.name = message;
      window.bugsnagClient &&
        window.bugsnagClient.notify(error, {
          metaData: responseData
        });
      handleLogout('error_in_subscription_cid');
    }

    const isVersionMismatch = message.includes('VERSION_MISMATCH');
    const error = new Error('Error in subscription response');
    error.name = isVersionMismatch
      ? 'VERSION_MISMATCH_ERROR_SUBSCRIPTION_CALLBACK'
      : 'UNKNOWN_ERROR_SUBSCRIPTION_CALLBACK';
    window.bugsnagClient &&
      window.bugsnagClient.notify(error, {
        metaData: responseData
      });
    if (message.includes('VERSION_MISMATCH')) {
      await handleErrorDSOs([responseData]);
    }
  }
  sync();
};

export async function initSubscription() {
  const firebaseSync = window.__getFeatureFlagFunc('firebase-subscription');
  if (firebaseSync) {
    initBgSyncServiceFirebase(subscriptionCallback);
  } else {
    initBgSyncService(subscriptionCallback);
  }
  sync();
}
